import { useLocalStorage } from "@elit-hair/elit-tools";
import { Button } from "@elit-hair/elit-ui";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import chatIcon from "@/assets/darwin-robot.svg";
import { ChatHeader } from "@/components/ai-chat/chat-header";
import { ChatInput } from "@/components/ai-chat/chat-input";
import { ChatMessages } from "@/components/ai-chat/chat-messages";
import { ErrorReportModal } from "@/components/ai-chat/error-report-modal";
import { useChatStream } from "@/components/ai-chat/use-chat-stream";
import { DraggableWrapper } from "@/components/draggable-wrapper";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { cn } from "@/lib/utils";
import { ChatMessage } from "@/types/chatbot.types";

type ChatbotWidgetProps = {
    className?: string;
};

export function ChatbotWidget({ className }: ChatbotWidgetProps) {
    const { t } = useTranslation();
    const [isInputVisible, setIsInputVisible] = useLocalStorage("chatbot-input-visible", false);
    const [isChatVisible, setIsChatVisible] = useLocalStorage("chatbot-chat-visible", false);
    const [isErrorReportModalOpen, setIsErrorReportModalOpen] = useState(false);
    const [reportedMessage, setReportedMessage] = useState<ChatMessage | null>(null);

    // Use stream hook only
    const { messages, input, handleInputChange, handleSubmit, clearHistory, isLoading, isStreaming, stopStreaming } =
        useChatStream();
    const widgetRef = useRef<HTMLDivElement>(null);

    const [mouseDownPos, setMouseDownPos] = useState<{ x: number; y: number } | null>(null);
    const [hasMoved, setHasMoved] = useState(false);
    const [windowSize, setWindowSize] = useState({
        width: typeof window !== "undefined" ? window.innerWidth : 1200,
        height: typeof window !== "undefined" ? window.innerHeight : 800,
    });

    const hasMessages = messages.length > 0;
    const isChatWindowOpen = isChatVisible || isInputVisible;
    const chatWindowWidth = 365;
    const chatWindowHeight = isChatVisible ? 380 : 32; // Chat window height
    const actualBottomOffset = 64; // bottom-16 = 4rem = 64px

    // Calculate bounds based on chat window state and current window size
    const dragBounds = isChatWindowOpen
        ? {
              minX: chatWindowWidth, // Button must be at least 365px from left edge so chat window fits
              maxX: windowSize.width - 56, // 56 is button width
              minY: chatWindowHeight + actualBottomOffset, // Button must be at least 564px from top so chat window fits (500px + 64px)
              maxY: windowSize.height - 56, // 56 is button height
          }
        : undefined; // No bounds when chat is closed

    const focusTextarea = () => {
        setTimeout(() => {
            const textarea = document.querySelector(".chatbot-input textarea");
            if (textarea instanceof HTMLTextAreaElement) {
                textarea.focus();
            }
        }, 100);
    };

    // Update window size on resize
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleDragStart = () => {
            // Dispatch custom event for DraggableWrapper
            window.dispatchEvent(new CustomEvent("chatbot-drag-start"));
        };

        const handleDragEnd = () => {
            // Dispatch custom event for DraggableWrapper
            window.dispatchEvent(new CustomEvent("chatbot-drag-end"));
        };

        window.addEventListener("chatbot-drag-start", handleDragStart);
        window.addEventListener("chatbot-drag-end", handleDragEnd);

        return () => {
            window.removeEventListener("chatbot-drag-start", handleDragStart);
            window.removeEventListener("chatbot-drag-end", handleDragEnd);
        };
    }, []);

    const handleMouseDown = (e: React.MouseEvent) => {
        setMouseDownPos({ x: e.clientX, y: e.clientY });
        setHasMoved(false);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (mouseDownPos && !hasMoved) {
            const deltaX = Math.abs(e.clientX - mouseDownPos.x);
            const deltaY = Math.abs(e.clientY - mouseDownPos.y);
            if (deltaX > 5 || deltaY > 5) {
                setHasMoved(true);
            }
        }
    };

    const handleButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        // Only prevent click if we've moved the mouse (indicating a drag)
        if (hasMoved) return;

        if (!hasMessages) {
            const newVisible = !isInputVisible;
            setIsInputVisible(newVisible);
            setIsChatVisible(false);
            if (newVisible) focusTextarea();
        } else {
            const newVisible = !isChatVisible;
            setIsChatVisible(newVisible);
            setIsInputVisible(false);
            if (newVisible) focusTextarea();
        }

        // Reset tracking
        setMouseDownPos(null);
        setHasMoved(false);
    };

    const handleFirstMessage = (e?: React.FormEvent<HTMLFormElement>) => {
        handleSubmit(e);
        setIsInputVisible(false);
        setIsChatVisible(true);
    };

    const handleClose = () => {
        setIsInputVisible(false);
        setIsChatVisible(false);
    };

    const handleClearHistory = () => {
        clearHistory();
        // Use setTimeout to ensure state updates after clearHistory completes
        setTimeout(() => {
            setIsChatVisible(false);
            setIsInputVisible(true);
            focusTextarea();
        }, 0);
    };

    const handleReportError = (message: ChatMessage) => {
        setReportedMessage(message);
        setIsErrorReportModalOpen(true);
    };

    const handleCloseErrorModal = () => {
        setIsErrorReportModalOpen(false);
        setReportedMessage(null);
    };

    return (
        <DraggableWrapper
            wrappedComponentName="ChatbotWidget"
            initialPosition={{
                x: windowSize.width - 80,
                y: windowSize.height - 80,
            }}
            bounds={dragBounds}
            persistPosition={true}
            storageKey={LOCAL_STORAGE_KEYS.AI_CHAT_BUTTON_POSITION.key}
            border={false}
            shadow={false}
            backgroundColor="transparent"
            backdropBlur={0}
            width={56}
            height={56}
            dragHandle=".chat-drag-handle"
        >
            <div ref={widgetRef} className={cn("relative", className)}>
                {/* Full Chat Window */}
                {isChatVisible && hasMessages && (
                    <div className="absolute bottom-[-0.5px] right-[-0.5px] w-[420px] max-w-[calc(100vw-2rem)] z-50 shadow-2xl overflow-hidden rounded-xl">
                        {/* Glassmorphism Container */}
                        <div className="bg-white/30 dark:bg-white/10 backdrop-blur-2xl border border-gray-200/50 dark:border-white/20 rounded-xl overflow-hidden flex h-[500px] max-h-[calc(100vh-6rem)]">
                            {/* Draggable Left Border */}
                            <div className="h-full w-full max-w-2 overflow-hidden chat-drag-handle" />

                            <div className="flex flex-col w-full">
                                {/* Draggable Top Border */}
                                <div className="h-full max-h-4 w-full overflow-hidden chat-drag-handle" />

                                {/* Draggable Chat Header */}
                                <div className="shrink-0 chat-drag-handle">
                                    <ChatHeader onClose={handleClose} onClearHistory={handleClearHistory} />
                                </div>

                                {/* Messages */}
                                <div className="flex-1 overflow-hidden min-h-0">
                                    <ChatMessages
                                        messages={messages}
                                        isLoading={isLoading || isStreaming}
                                        onReportError={handleReportError}
                                    />
                                </div>

                                {/* Input */}
                                <div className="shrink-0">
                                    <ChatInput
                                        value={input}
                                        onChange={handleInputChange}
                                        onSubmit={handleSubmit}
                                        isLoading={isLoading}
                                        isStreaming={isStreaming}
                                        onStop={stopStreaming}
                                        placeholder={t("type_your_message")}
                                        className="chatbot-input"
                                    />
                                    {/* Draggable Bottom Border */}
                                    <div className="h-full max-h-4 w-full overflow-hidden chat-drag-handle" />
                                </div>
                            </div>
                            {/* Draggable Right Border */}
                            <div className="h-full w-full max-w-2 overflow-hidden chat-drag-handle" />
                        </div>
                    </div>
                )}

                {/* Input Only Window */}
                {isInputVisible && !hasMessages && (
                    <div className="absolute bottom-16 right-0 w-[420px] max-w-[calc(100vw-2rem)] z-50 shadow-2xl rounded-xl">
                        {/* Glassmorphism Container */}
                        <div className="bg-white/30 dark:bg-white/10 backdrop-blur-2xl border border-gray-200/50 dark:border-white/20 rounded-xl overflow-hidden">
                            <ChatInput
                                value={input}
                                onChange={handleInputChange}
                                onSubmit={handleFirstMessage}
                                isLoading={isLoading}
                                isStreaming={isStreaming}
                                onStop={stopStreaming}
                                placeholder={t("ask_me_anything")}
                                className="chatbot-input"
                            />
                        </div>
                    </div>
                )}

                {/* Chat Button */}
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleButtonClick}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    className={cn(
                        "w-14 h-14 p-0 bg-muted border border-primary/50 text-primary/50",
                        isChatVisible && "bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500",
                        "shadow-lg hover:shadow-xl",
                        "flex items-center justify-center rounded-2xl",
                        "hover:scale-105 transition-all duration-200",
                        "focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:ring-offset-2",
                        "relative z-sticky chat-drag-handle"
                    )}
                >
                    <img src={chatIcon} alt="Chat-Head-Icon" className="w-10 h-10" />
                </Button>
            </div>

            {/* Error Report Modal */}
            <ErrorReportModal
                isOpen={isErrorReportModalOpen}
                onClose={handleCloseErrorModal}
                conversation={messages.map((msg) => ({
                    role: msg.role,
                    content: msg.content,
                }))}
                reportedMessage={
                    reportedMessage
                        ? {
                              role: reportedMessage.role,
                              content: reportedMessage.content,
                          }
                        : undefined
                }
            />
        </DraggableWrapper>
    );
}
